import React, { useState, useEffect } from "react";
import { FiCalendar, FiMenu, FiX, FiExternalLink, FiLoader, FiCloudOff } from "react-icons/fi";
import { FaToggleOn, FaToggleOff,  } from "react-icons/fa6";
import { AppData, FetchError } from "../../../types";
import { useCalendarGroupToggles } from "../DataProvider/CalenderGroupContext/CalenderGroupContext";
import { TodayIcon } from "./TodayIcon/TodayIcon";
import { LongPressRefresh } from "./LongPressRefresh/LongPressRefresh"
import "./AppHeaderMenu.css";

type AppMenuProps = {
  onHeaderClick: (newDay: string) => void;
  displayedDay: string;
  vereinsData: AppData;
  fetching: boolean
  fetchErrors: FetchError[];
};

const AppHeaderMenu: React.FC<AppMenuProps> = ({ onHeaderClick, displayedDay, vereinsData, fetching, fetchErrors }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  // Group calendars by Altersklasse
  const grouped = vereinsData.calendars.reduce((acc, calendar) => {
    const key = calendar.Team.Altersklasse;
    if (!acc[key]) acc[key] = [];
    acc[key].push(calendar);
    return acc;
  }, {} as Record<string, typeof vereinsData.calendars>);

  // Context state
  const { toggles, setToggle } = useCalendarGroupToggles();

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <>
      <div className={`drawer ${menuOpen ? "open" : ""}`}>
        <button className="drawer-close" onClick={() => setMenuOpen(false)}>
          <FiX />
        </button>
        <div className="drawer-content">
          <h3>Kalenderübersicht</h3>
          <div className="calendar-group-list-container">
            {Object.entries(grouped).map(([altersklasse, calendars]) => { 
              const isToggled = toggles[altersklasse] ?? true;
              const calendarOffline = calendars.reduce((acc, calendar) => {
                acc[calendar.Team.Name] = fetchErrors.some(
                  e => e.team === calendar.Team.Name
                );
                return acc;
              }, {} as Record<string, boolean>);
              return (
                <div key={altersklasse} className="calender-group">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: "0.5rem",
                      marginBottom: "8px",
                    }}
                    className={ `calender-group-heading ${altersklasse.toLowerCase()}` }
                  >
                    <h4
                      style={{
                        margin: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        color: isToggled ? "inherit" : "#999"
                      }}
                    >
                      <button
                        onClick={() => setToggle(altersklasse, !isToggled)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                          lineHeight: 1,
                          color: isToggled ? "inherit" : "#999"
                        }}
                        aria-label={`Toggle ${altersklasse}`}
                      >
                        {isToggled ? (
                          <FaToggleOn size={24} style={{ verticalAlign: "middle" }} />
                        ) : (
                          <FaToggleOff size={24} style={{ verticalAlign: "middle" }} />
                        )}
                      </button>
                      {altersklasse}
                    </h4>
                  </div>

                  <ul
                    className="calendar-list"
                    style={{
                      color: isToggled ? "inherit" : "#999",
                    }}
                  >
                    {calendars.map((calendar, idx) => (
                      <li key={idx}>
                        <span
                          title={ `${calendarOffline[calendar.Team.Name] ? "Die Termine dieses Kalenders konnten nicht geladen werden" : "" }` }
                          style={{
                            color: calendarOffline[calendar.Team.Name] ? "#999" : "inherit" ,
                          }}>
                          {calendar.Team.Name} &nbsp;
                          {fetching ? 
                            <FiLoader/> : calendarOffline[calendar.Team.Name] ? <FiCloudOff /> : "" } 
                        </span>
                        <div>
                          <a
                            className="icon-link"
                            href={calendar.Team.URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Zur Teamseite / Homepage"
                          >
                            <FiExternalLink />
                          </a>
                          <a
                            className="icon-link"
                            href={calendar.Events.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Kalender abonnieren"
                          >
                            <FiCalendar />
                          </a>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {menuOpen && (
        <div className="drawer-backdrop" onClick={() => setMenuOpen(false)} />
      )}
      <header className="app-header">
        <TodayIcon displayedDay={displayedDay} onClick={onHeaderClick} />
        <div>
          <LongPressRefresh holdMs={700}>
            <h3>{vereinsData.verein.LongName}</h3>
          </LongPressRefresh>
          <div className="displayed-calenders">
            {
            Object.values(toggles).every(v => v)
              ? null
              : Object.entries(toggles)
                  .filter(([_, value]) => value)
                  .map(([key], idx, arr) => (
                    <span key={key}>
                      {key}
                      {idx < arr.length - 1 && " - "}
                    </span>
                  ))
            }
          </div>
        </div>
        <button
          className="menu-button"
          onClick={() => setMenuOpen(true)}
          aria-label="Menü öffnen"
        >
          <FiMenu />
        </button>
      </header>
    </>
  );
};

export default AppHeaderMenu;